// ============================================================================
// AFB-0042 — selection-drag must not dismiss a modal.
//
// House modals are hand-rolled overlays: <div className="fixed inset-0 …"
// onClick={onClose}> with the content stopping propagation. Browsers dispatch
// `click` on the NEAREST COMMON ANCESTOR of the mousedown and mouseup targets,
// so a text-selection drag that starts inside an <input> and ends over the
// backdrop fires `click` on the overlay itself → the modal closed and the
// user's entered data was lost (reporter: Jack Stuber).
//
// Fix: spread {...overlayClickClose(onClose)} on the overlay INSTEAD of
// onClick={onClose}. It only dismisses when the pointer sequence genuinely
// began AND ended on the overlay and there is no active text selection.
// A real backdrop click still closes; a selection drag never does.
// ============================================================================

import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';

const DOWN_FLAG = 'overlayPointerDownSelf';

export function overlayClickClose(onDismiss: () => void): {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onClick: (e: ReactMouseEvent<HTMLElement>) => void;
} {
  return {
    onPointerDown: (e) => {
      // Remember whether the press STARTED on the overlay itself (not on a
      // child). Stored on the element so no hook/state plumbing is needed.
      e.currentTarget.dataset[DOWN_FLAG] = e.target === e.currentTarget ? '1' : '0';
    },
    onClick: (e) => {
      const startedOnOverlay = e.currentTarget.dataset[DOWN_FLAG] === '1';
      delete e.currentTarget.dataset[DOWN_FLAG];
      // Click must land on the overlay itself…
      if (e.target !== e.currentTarget) return;
      // …the press must have started there too (a drag that began inside the
      // content — i.e. a text selection — must never dismiss)…
      if (!startedOnOverlay) return;
      // …and an active selection means the user is mid-selection, not closing.
      if (window.getSelection()?.toString()) return;
      onDismiss();
    },
  };
}
